import { AxiosError } from "axios";
import { CarrierPayload, CarrierResponse } from "./request/request";

/**
 * The create/edit form of the Carriers admin (CTX-786), kept free of React so
 * its rules can be tested on their own.
 */
export interface CarrierFormState {
  name: string;
  code: string;
  available: boolean;
}

export type CarrierFormErrors = Partial<Record<keyof CarrierFormState, string>>;

/** The backend's limits (chopper-backend CTX-785). */
const NAME_MAX = 255;
const CODE_MAX = 32;

export const emptyCarrierForm: CarrierFormState = {
  name: "",
  code: "",
  available: true,
};

export function formFromCarrier(carrier: CarrierResponse): CarrierFormState {
  return {
    name: carrier.name,
    code: carrier.code ?? "",
    available: carrier.available,
  };
}

export function validateCarrierForm(form: CarrierFormState): CarrierFormErrors {
  const errors: CarrierFormErrors = {};
  const name = form.name.trim();
  if (!name) errors.name = "Enter the carrier's name";
  else if (name.length > NAME_MAX) errors.name = `Up to ${NAME_MAX} characters`;
  if (form.code.trim().length > CODE_MAX) errors.code = `Up to ${CODE_MAX} characters`;
  return errors;
}

function toFullPayload(form: CarrierFormState): CarrierPayload {
  return {
    name: form.name.trim(),
    // An empty code is "no code", which never collides on the unique index.
    code: form.code.trim() || null,
    available: form.available,
  };
}

/** Every field on create; on edit, only the ones that changed. */
export function buildCarrierPayload(
  form: CarrierFormState,
  original?: CarrierFormState
): CarrierPayload {
  const full = toFullPayload(form);
  if (!original) return full;

  const before = toFullPayload(original);
  const changed: CarrierPayload = {};
  (Object.keys(full) as Array<keyof CarrierPayload>).forEach((key) => {
    if (full[key] !== before[key]) Object.assign(changed, { [key]: full[key] });
  });
  return changed;
}

interface ApiErrorBody {
  code?: string;
  field?: "name" | "code";
  message?: string;
  serviceCount?: number;
}

export interface CarrierApiError {
  code: string | null;
  message: string;
  /** The field a DUPLICATE_CARRIER points at, to mark it on the form. */
  field: keyof CarrierFormState | null;
  /** How many services hold a carrier that CARRIER_IN_USE refused to delete. */
  serviceCount: number | null;
}

const DUPLICATE_MESSAGES: Record<"name" | "code", string> = {
  name: "Another carrier already uses this name.",
  code: "Another carrier already uses this code. Pick a different code or leave it empty.",
};

const failure = (message: string, code: string | null = null): CarrierApiError => ({
  code,
  message,
  field: null,
  serviceCount: null,
});

/** What the admin reads when the backend refuses a save or a delete. */
export function describeCarrierError(error: unknown): CarrierApiError {
  if (!(error instanceof AxiosError)) {
    return failure("Could not reach the server. Try again.");
  }
  const body = error.response?.data as ApiErrorBody | undefined;
  if (body?.code === "DUPLICATE_CARRIER" && body.field) {
    return { ...failure(DUPLICATE_MESSAGES[body.field], body.code), field: body.field };
  }
  if (body?.message) {
    return {
      ...failure(body.message, body.code ?? null),
      serviceCount: typeof body.serviceCount === "number" ? body.serviceCount : null,
    };
  }
  if (error.response) return failure(`The server answered ${error.response.status}`);
  return failure("Could not reach the server. Try again.");
}
